// alterSequence — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'alter-sequence' })` op (DSE2 §3.D4).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice D3 — Sequence UI.
//
// Per-engine surface (per DSE2 dialect.sequence):
//   - mssql / pgsql / oracle: ALTER SEQUENCE name <change-clauses>
//   - mysql + sqlite: unsupported — engines reject the parent op upstream.

import type { SqlEngine, QualifiedName } from '../../../../sql-grammar/model';
import type {
  DdlEdit,
  SequenceChange,
} from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

import { getCreateSequenceUnsupportedTooltip } from './createSequence';

export interface AlterSequenceArgs {
  readonly sequenceName: QualifiedName;
  readonly change: SequenceChange;
}

export function buildAlterSequenceEdit(args: AlterSequenceArgs): DdlEdit {
  return {
    op: 'alter-sequence',
    sequenceName: args.sequenceName,
    change: args.change,
  };
}

export async function commitAlterSequence(
  ctx: VqeDdlEditContext,
  args: AlterSequenceArgs,
): Promise<void> {
  const edit = buildAlterSequenceEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const ALTER_SEQUENCE_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'mssql', 'postgres', 'oracle',
] as const);

export function isAlterSequenceSupported(engine: SqlEngine): boolean {
  return ALTER_SEQUENCE_SUPPORTED.has(engine);
}

export function getAlterSequenceUnsupportedTooltip(engine: SqlEngine): string {
  return getCreateSequenceUnsupportedTooltip(engine);
}

/** A change set is "non-empty" when at least one clause is set — DSE2
 *  rejects empty ALTER SEQUENCE at synth-time, so the form pre-flights
 *  the same condition for a friendlier error. */
export function isNonEmptySequenceChange(change: SequenceChange): boolean {
  return (
    change.incrementBy !== undefined
    || change.minValue !== undefined
    || change.maxValue !== undefined
    || change.cycle !== undefined
    || change.cache !== undefined
    || change.restartWith !== undefined
    || change.oracleOrder !== undefined
    || change.oracleKeep !== undefined
  );
}

export function validateAlterSequenceForEngine(
  engine: SqlEngine,
  args: AlterSequenceArgs,
): string | null {
  if (!isAlterSequenceSupported(engine)) {
    return getAlterSequenceUnsupportedTooltip(engine);
  }
  if (args.sequenceName.name.trim().length === 0) {
    return 'Sequence name is required.';
  }
  if (!isNonEmptySequenceChange(args.change)) {
    return 'At least one change is required.';
  }
  if (engine !== 'oracle') {
    if (args.change.oracleOrder !== undefined) {
      return `${engine} does not support ORDER / NOORDER (Oracle-only).`;
    }
    if (args.change.oracleKeep !== undefined) {
      return `${engine} does not support KEEP / NOKEEP (Oracle-only).`;
    }
  }
  if (
    args.change.minValue !== undefined && args.change.minValue !== null
    && args.change.maxValue !== undefined && args.change.maxValue !== null
    && args.change.minValue > args.change.maxValue
  ) {
    return 'MINVALUE must be ≤ MAXVALUE.';
  }
  return null;
}
